import { useState, useEffect, useMemo, useRef, useId } from "react";
import { api } from "../api.js";
import ErrorBanner from "./ErrorBanner.jsx";
import {
  isLocalId,
  updateQueuedEntry,
  removeQueuedEntry,
} from "../offline.js";

// Bottom-sheet entry form. Fields match the paper form exactly.
export default function EntryModal({
  mode,
  entry,
  defaultDate,
  priorEntries,
  onClose,
  onSaved,
  onDeleted,
}) {
  const isEdit = mode === "edit";
  const [date, setDate] = useState(entry?.date || defaultDate);
  const [origin, setOrigin] = useState(entry?.origin || "");
  const [destination, setDestination] = useState(entry?.destination || "");
  const [miles, setMiles] = useState(entry?.miles?.toString() ?? "");
  const [parking, setParking] = useState(
    entry?.parking?.toFixed(2) ?? "0.00"
  );
  const [tolls, setTolls] = useState(entry?.tolls?.toFixed(2) ?? "0.00");
  const [purpose, setPurpose] = useState(entry?.purpose || "");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const firstFieldRef = useRef(null);
  const uid = useId();

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  // Autocomplete lists: unique values from prior entries
  const originOptions = useMemo(
    () => uniq(priorEntries.map((e) => e.origin)).slice(0, 20),
    [priorEntries]
  );
  const destOptions = useMemo(
    () => uniq(priorEntries.map((e) => e.destination)).slice(0, 20),
    [priorEntries]
  );
  const purposeOptions = useMemo(
    () => uniq(priorEntries.map((e) => e.purpose)).slice(0, 20),
    [priorEntries]
  );

  async function save() {
    setErr(null);
    setBusy(true);
    try {
      const payload = {
        date,
        origin: origin.trim(),
        destination: destination.trim(),
        miles: Number(miles),
        parking: Number(parking || 0),
        tolls: Number(tolls || 0),
        purpose: purpose.trim(),
      };
      if (!payload.date) throw new Error("Date is required");
      if (!payload.origin) throw new Error("Origin is required");
      if (!payload.destination) throw new Error("Destination is required");
      if (!Number.isFinite(payload.miles) || payload.miles < 0)
        throw new Error("Enter valid miles");
      if (!payload.purpose) throw new Error("Purpose is required");

      // Warn on likely duplicate: same date + origin + destination already exists.
      const dup = priorEntries.find(
        (e) =>
          e.id !== entry?.id &&
          e.date === payload.date &&
          e.origin === payload.origin &&
          e.destination === payload.destination
      );
      if (dup && !confirm(`A trip from ${payload.origin} to ${payload.destination} on ${payload.date} already exists. Save anyway?`)) {
        setBusy(false);
        return;
      }

      if (isEdit) {
        // Local-only trips (still in the IDB queue) are mutated in place so
        // the eventual POST carries the edited payload — no server call yet.
        if (isLocalId(entry.id)) {
          await updateQueuedEntry(entry.id, payload);
        } else {
          await api.updateEntry(entry.id, payload);
        }
      } else {
        await api.createEntry(payload);
      }
      await onSaved();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm("Delete this trip?")) return;
    setBusy(true);
    try {
      if (isLocalId(entry.id)) {
        await removeQueuedEntry(entry.id);
      } else {
        await api.deleteEntry(entry.id);
      }
      await onDeleted();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center"
      onClick={onClose}
    >
      <div
        className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-white p-4 shadow-xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">
            {isEdit ? "Edit trip" : "Add trip"}
          </h2>
          <button
            onClick={onClose}
            className="tap rounded-md px-3 text-slate-500 active:bg-slate-100"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="label" htmlFor="date">Date</label>
            <input
              id="date"
              ref={firstFieldRef}
              type="date"
              className="field"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="origin">Origin</label>
            <input
              id="origin"
              list={uid + "-origin"}
              className="field"
              value={origin}
              onChange={(e) => setOrigin(e.target.value)}
              placeholder="e.g. Main Office"
              autoComplete="off"
              required
            />
            <datalist id={uid + "-origin"}>
              {originOptions.map((o) => <option key={o} value={o} />)}
            </datalist>
          </div>
          <div>
            <label className="label" htmlFor="destination">Destination</label>
            <input
              id="destination"
              list={uid + "-dest"}
              className="field"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              placeholder="e.g. Client A - Buffalo"
              autoComplete="off"
              required
            />
            <datalist id={uid + "-dest"}>
              {destOptions.map((o) => <option key={o} value={o} />)}
            </datalist>
          </div>
          <div>
            <label className="label" htmlFor="miles">Miles</label>
            <input
              id="miles"
              className="field tabular-nums"
              inputMode="decimal"
              value={miles}
              onChange={(e) => setMiles(e.target.value)}
              placeholder="0.0"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label" htmlFor="parking">Parking ($)</label>
              <input
                id="parking"
                className="field tabular-nums"
                inputMode="decimal"
                value={parking}
                onChange={(e) => setParking(e.target.value)}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div>
              <label className="label" htmlFor="tolls">Tolls ($)</label>
              <input
                id="tolls"
                className="field tabular-nums"
                inputMode="decimal"
                value={tolls}
                onChange={(e) => setTolls(e.target.value)}
                onFocus={(e) => e.target.select()}
              />
            </div>
          </div>
          <div>
            <label className="label" htmlFor="purpose">Purpose for travel</label>
            <textarea
              id="purpose"
              list={uid + "-purpose"}
              className="field min-h-[72px]"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="e.g. Home visit — intake assessment"
              required
            />
            <datalist id={uid + "-purpose"}>
              {purposeOptions.map((o) => <option key={o} value={o} />)}
            </datalist>
          </div>
          <p className="text-xs text-slate-500">
            Attach any parking / toll receipts by email to your supervisor after
            submitting.
          </p>

          <ErrorBanner message={err} />

          <div className="flex gap-2 pt-2">
            {isEdit && (
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                className="tap rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 active:bg-red-100"
              >
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="btn-primary flex-1"
            >
              {busy ? "Saving…" : isEdit ? "Save changes" : "Add trip"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function uniq(arr) {
  return [...new Set(arr.filter(Boolean))];
}
